import React, { useState } from 'react';

import DataTableContainer from '../../components/DataTableContainer';
import MyDataTable from '../../components/MyDataTable';
import { ServerFarmInterface, ServerPool, streamServersService } from '../../services/central-api/stream-servers';
import { translate } from '../../services/translate';
import { View } from 'react-native';
import Badge from '../../components/Badge';
import DropdownButton from '../../components/DropdownButton';
import AlertModal from '../../components/AlertModal';
import { TranslateRich } from '../../components/TranslateRich';

interface ServerFarmDetailServerPoolsParams {
    serverFarm: ServerFarmInterface;
    setServerPoolId: (serverPoolId: string | null) => void;
    setTimelineProviderId: (timelineProviderId: number | null) => void;
    setVideoStorageProviderId: (videoStorageProvider: number | null) => void;
    setSelectedTab: (selectedTab: 'serverPools' | 'timelineProviders' | 'videoStorageProviders' | 'servers' | null) => void;
    refetch: () => void;
}

export default function ServerFarmDetailServerPools(params: ServerFarmDetailServerPoolsParams) {
    const [isModalVisible, setModalVisible] = useState(false);
    const [selectedServerPool, setSelectedServerPool] = useState<ServerPool | null>(null);

    return (<>
        <DataTableContainer
            dataTable={<MyDataTable
                data={params.serverFarm.serverPools}
                pointerOnHover
                onRowClicked={(row) => {
                    params.setServerPoolId(row.id);
                    params.setTimelineProviderId(null);
                    params.setVideoStorageProviderId(null);
                    params.setSelectedTab(null);
                }}
                columns={[{
                    name: translate('id'),
                    selector: row => row.id,
                }, {
                    name: translate('provider'),
                    selector: row => row.cloudProvider,
                }, {
                    name: translate('type'),
                    selector: row => row.type,
                }, {
                    name: translate('servers'),
                    selector: row => row.serverCount,
                    center: true,
                }, {
                    name: translate('defaultForCameras'),
                    cell: row => {
                        if (row.id === params.serverFarm.defaultServerRtspPoolId) {
                            return <View style={{ justifyContent: 'center', alignItems: 'center' }}>
                                <Badge mode='SQUARE' key={row.id} color='green'>
                                    {translate('defaultRtspPool')}
                                </Badge>
                            </View>;
                        }

                        if (row.id === params.serverFarm.defaultServerRtmpPoolId) {
                            return <View style={{ justifyContent: 'center', alignItems: 'center' }}>
                                <Badge mode='SQUARE' key={row.id} color='green'>
                                    {translate('defaultRtmpPool')}
                                </Badge>
                            </View>;
                        }

                        if (row.id === params.serverFarm.defaultNoDvrServerRtspPoolId) {
                            return <View style={{ justifyContent: 'center', alignItems: 'center' }}>
                                <Badge mode='SQUARE' key={row.id} color='green'>
                                    {translate('defaultNoDvrRtspPool')}
                                </Badge>
                            </View>;
                        }

                        return <></>;
                    },
                    wrap: true,
                }, {
                    name: '',
                    button: true,
                    wrap: true,
                    cell: (row) => {
                        return (
                            <DropdownButton
                                size={320}
                                offsetX={-168}
                                actions={[{
                                    content: ((row: ServerPool) => {
                                        if (row.type === 'rtsp') {
                                            return translate('setDefaultRtspPool');
                                        }
                                        if (row.type === 'rtmp') {
                                            return translate('setDefaultRtmpPool');
                                        }
                                        if (row.type === 'rtsp_no_dvr') {
                                            return translate('setDefaultNoDvrRtspPool');
                                        }
                                    })(row),
                                    omit: (row.id === params.serverFarm.defaultServerRtspPoolId
                                        || row.id === params.serverFarm.defaultServerRtmpPoolId
                                        || row.id === params.serverFarm.defaultNoDvrServerRtspPoolId),
                                    onPress: () => {
                                        setSelectedServerPool(row);
                                        setModalVisible(true);
                                    },
                                }]}
                            />
                        );
                    }
                }]}
            />}
        />
        <AlertModal
            setModalVisible={setModalVisible}
            isModalVisible={isModalVisible}
            title={translate('attention')}
            type='warning'
            customButtonLabel={translate('confirm')}
            description={<>
                <TranslateRich
                    i18nKey={'setServerPoolAsDefault'}
                    values={{
                        serverPoolId: <strong>{selectedServerPool?.id || ''}</strong>,
                        serverPoolType: <strong>{selectedServerPool?.type.toUpperCase() || ''}</strong>,
                    }}
                />
            </>}
            onSubmit={async () => {
                try {
                    if (!selectedServerPool) {
                        return;
                    }
                    await streamServersService.setServerPoolAsDefault(selectedServerPool.id);
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
        />
    </>);
}
